import { useMutation } from '@tanstack/react-query';
import { clsx } from 'clsx';
import isEmpty from 'lodash/isEmpty';
import { memo, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FlowIndex } from '~/components/FlowIndex';
import InfoIconTooltip from '~/components/InfoIconTooltip';
import RemainingAmountTextField from '~/components/RemainingAmountTextField';
import CategoryItem from '~/components/categorization/data-display/CategoryItem';
import AlertMessage from '~/components/data-display/AlertMessage';
import SimpleTooltip from '~/components/data-display/SimpleTooltip';
import Checkbox from '~/components/inputs/Checkbox/Checkbox';
import DocumentPreviewDialogLink from '~/components/invoices/PreviewInvoice/DocumentPreviewDialogLink';
import { HStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import { useDeleteReconciliationMutation } from '~/services/reconciliation';
import { useShowReconciledFlows } from '~/state/modals/reconcileTransactionModal';
import { Document, DocumentStatus } from '~/types/document';
import { Flow, FlowPaymentStatus, FlowWithDocument } from '~/types/flow';
import { Money } from '~/types/money';
import { Reconciliation } from '~/types/reconciliation';
import { Transaction } from '~/types/transaction';
import { isDefined } from '~/types/typeGuards';
import { canPreviewDocument } from '~/utils/document';
import { flowAmount } from '~/utils/flow';
import { formatFromISOToDisplay } from '~/utils/formatDate';
import { formatMoney, negateMoney, absMoney } from '~/utils/money';
import { filterVerifiedReconciliations, hasVerifiedReconciliation } from '~/utils/reconciliations';
import { AcceptButton, CloseButton, DissociateButton, EditButton, SplitButton } from './Buttons';
import { useReconcileModalContext } from './ModalContext';
import { DocumentDescriptionText, EllipsedText } from './ReconciliationCommon';
import styles from './SearchResultCard.module.scss';

const DocumentResume = ({ document }: { document: Document }) => {
  const { t } = useTranslation('reconciliations');

  return (
    <div className={styles.documentResumeContainer}>
      <div className={styles.documentResumeText}>
        <EllipsedText variant="body1" weight="semibold" element="span">
          {document.counterpart?.companyName ?? document.counterpartName}
        </EllipsedText>
        <HStack gap={1} align="center">
          {canPreviewDocument(document) && (
            <SimpleTooltip title={t('tooltip.view_document')} mode="div" delay={250} closeDelay={100}>
              <DocumentPreviewDialogLink document={document} />
            </SimpleTooltip>
          )}
          <EllipsedText variant="body2" weight="regular" element="span" secondaryColor>
            <DocumentDescriptionText document={document} />
          </EllipsedText>
        </HStack>
      </div>
      <div className={styles.documentResumeCategory}>
        <CategoryItem category={document.category} subcategory={document.subcategory} fullWidth={false} />
        <Text variant="body2" weight="semibold" align="right">
          {formatMoney(
            document.isInflow ? absMoney(document.grossAmount) : negateMoney(absMoney(document.grossAmount)),
          )}
        </Text>
      </div>
    </div>
  );
};

const FlowResumeDissociating = ({
  flow,
  onClose,
  onSuccess,
}: {
  flow: FlowWithDocument;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const { t } = useTranslation('reconciliations');
  const deleteReconciliation = useDeleteReconciliationMutation();
  const flowStatus = useFlowStatusText(flow);

  const removeReconciliations = useMutation({
    mutationFn: async () => {
      for (const r of filterVerifiedReconciliations(flow.reconciliations)) {
        await deleteReconciliation.mutateAsync({ reconciliationId: r.id, context: 'transaction' });
      }
      onSuccess();
      onClose();
    },
  });

  return (
    <div className={clsx(styles.resumeRow, styles.resumeDissociatingRow)}>
      <Checkbox checked={false} isDisabled onChange={() => {}} />
      <Text variant="body2" weight="semibold" secondaryColor>
        <FlowIndex flow={flow} />
      </Text>

      <HStack gap={4} align="center">
        <ReconciliatedFlowDescription flow={flow} />
        <Text variant="body2" weight="regular" secondaryColor>
          {flowStatus}
        </Text>
        {removeReconciliations.isIdle && (
          <div className={styles.removeIdle}>
            <Text variant="body2" weight="semibold">
              {t('dissociate_question')}
            </Text>
            <AcceptButton onClick={() => removeReconciliations.mutate()} />
            <CloseButton onClick={onClose} />
          </div>
        )}

        {removeReconciliations.isPending && (
          <Text variant="body2" weight="semibold">
            {t('loading')}
          </Text>
        )}
      </HStack>

      <Text variant="body2" weight="regular" align="right">
        {formatMoney(flowAmount(flow))}
      </Text>
    </div>
  );
};

const FlowResume = ({ flow, onDissociateSuccess }: { flow: FlowWithDocument; onDissociateSuccess: () => void }) => {
  const [state, setState] = useState<{ state: 'show' } | { state: 'dissociate' } | { state: 'split'; amount?: Money }>({
    state: 'show',
  });

  const gotoShow = () => setState(() => ({ state: 'show' }));

  switch (state.state) {
    case 'show':
      return (
        <FlowResumeShow
          flow={flow}
          onSplit={(amount?: Money) => setState(() => ({ state: 'split', amount }))}
          onDissociate={() => setState(() => ({ state: 'dissociate' }))}
        />
      );
    case 'dissociate':
      return <FlowResumeDissociating flow={flow} onClose={gotoShow} onSuccess={onDissociateSuccess} />;
    case 'split':
      return <FlowResumeSplitting flow={flow} onClose={gotoShow} defaultValue={state.amount} />;
  }
};

const FlowResumeShow = ({
  flow,
  onSplit,
  onDissociate,
}: {
  flow: FlowWithDocument;
  onSplit: (amount?: Money) => void;
  onDissociate: () => void;
}) => {
  const { isSelected, getProps } = useReconcileModalContext();

  const selected = isSelected(flow);
  const props = getProps(flow);

  const isSplit = selected && props?.split;

  return isSplit ? (
    <FlowResumeShowSplit flow={flow} onSplit={onSplit} />
  ) : (
    <FlowResumeShowPlain flow={flow} onSplit={onSplit} onDissociate={onDissociate} />
  );
};

const FlowResumeSplitting = ({
  flow,
  onClose,
  defaultValue,
}: {
  flow: FlowWithDocument;
  onClose: () => void;
  defaultValue?: Money;
}) => {
  const { t } = useTranslation('reconciliations');
  const { splitFlow } = useReconcileModalContext();

  const form = useForm({
    defaultValues: { amount: defaultValue ?? { amount: '', currency: flow.amount.currency } },
  });

  const amount = form.watch('amount');
  const hasValue = Boolean(amount.amount);

  const onSubmit = ({ amount }: { amount: Money }) => {
    if (!amount.amount) return;
    splitFlow(flow, amount);
    onClose();
  };

  return (
    <div className={clsx(styles.resumeRow, styles.resumeSplittingRow)}>
      <Checkbox checked={false} isDisabled />
      <Text variant="body2" weight="semibold" secondaryColor>
        <FlowIndex flow={flow} />
      </Text>

      <Text variant="body2" weight="regular" secondaryColor>
        {t('flow_payment_date')} {formatFromISOToDisplay(flow.expectedPaymentDate)}
      </Text>

      <form onSubmit={form.handleSubmit(onSubmit)} className={styles.splittingForm}>
        <Controller
          control={form.control}
          name="amount"
          render={({ field }) => (
            <RemainingAmountTextField
              value={field.value}
              limit={flow.amount}
              placeholder={t('reconcile_amount')}
              onChange={field.onChange}
            />
          )}
        />
        <AcceptButton disabled={!hasValue} />
        <CloseButton onClick={onClose} />
      </form>
      <Text variant="body2" weight="regular" align="right">
        {formatMoney(flowAmount(flow))}
      </Text>
    </div>
  );
};

const FlowResumeShowSplit = ({ flow, onSplit }: { flow: FlowWithDocument; onSplit: (amount?: Money) => void }) => {
  const { t } = useTranslation('reconciliations');
  const { removeFlow, getProps } = useReconcileModalContext();

  const props = getProps(flow);

  const amount = props?.amount ?? flowAmount(flow);
  const remainingAmount = props?.remainingAmount;

  return (
    <div className={clsx(styles.resumeRow, styles.resumeShowSplitRow)}>
      <Checkbox checked onChange={() => removeFlow(flow.id)} />
      <Text variant="body2" weight="semibold">
        {t('advance_amount')}
      </Text>

      <HStack gap={1} align="center">
        <Text variant="body2" weight="regular" element={'span'} secondaryColor>
          {t('flow_payment_date')} {formatFromISOToDisplay(flow.expectedPaymentDate)}
        </Text>
        <EditButton onClick={() => onSplit(amount)} />
      </HStack>

      <div />
      <div className={styles.amountContainer}>
        <Amount amount={flowAmount(flow)} weight="regular" />
      </div>

      <div className={styles.rowDivider} />
      <div className={styles.gridColumn1_4} />
      <Text variant="body2" weight="semibold" align="left">
        {t('reconciled')}
      </Text>

      <div className={styles.amountContainer}>
        <Text variant="body2" weight="regular" align="right">
          {formatMoney(amount)}
        </Text>
      </div>

      <div className={styles.rowDivider} />

      <div className={styles.gridColumn1_3} />
      <div>
        <Text variant="body2" weight="regular" secondaryColor>
          {t('split_tooltip')}
        </Text>
      </div>

      <Text variant="body2" weight="semibold" align="left">
        {t('to_pay')}
      </Text>
      <div className={styles.amountContainer}>
        <Text variant="body2" weight="regular" align="right">
          {remainingAmount && formatMoney(flow.document.isInflow ? remainingAmount : negateMoney(remainingAmount))}
        </Text>
      </div>
    </div>
  );
};

const isFlowToDissociate = (flow: Flow, transaction: Transaction) => {
  const transactionRec = filterVerifiedReconciliations(transaction.reconciliations).map((d) => d.id);

  const otherReconciliations = filterVerifiedReconciliations(flow.reconciliations).filter(
    (r) => !transactionRec.includes(r.id),
  );

  return !isEmpty(otherReconciliations);
};

const useFlowStatusText = (flow: FlowWithDocument) => {
  const { t } = useTranslation('reconciliations');

  if (flow.document.status === DocumentStatus.Draft) return t('reconcile_transaction_modal.draft_label');

  return flow.paymentStatus === FlowPaymentStatus.Paid
    ? t('reconcile_transaction_modal.paid_label')
    : t('reconcile_transaction_modal.unpaid_label');
};

const FlowResumeShowPlain = ({
  flow,
  onSplit,
  onDissociate,
}: {
  flow: FlowWithDocument;
  onSplit: () => void;
  onDissociate: () => void;
}) => {
  const { t } = useTranslation('reconciliations');
  const { transaction, selectFlow, isSelected, removeFlow, getProps } = useReconcileModalContext();
  const flowStatus = useFlowStatusText(flow);

  const selected = isSelected(flow);

  const toDissociate = isFlowToDissociate(flow, transaction);
  const isReconciledWithRegisteredTransaction = filterVerifiedReconciliations(flow.reconciliations).some((r) =>
    isDefined(r.transaction?.bookkeepingTransaction),
  );

  const props = getProps(flow);
  const amount = props?.amount ?? flowAmount(flow);

  const isCheckable = selected || !toDissociate;
  return (
    <div className={clsx(styles.resumeRow, styles.resumeShowPlainRow, { [styles.rowSelected!]: selected })}>
      <Checkbox
        checked={selected}
        isDisabled={!isCheckable}
        onChange={() => (selected ? removeFlow(flow.id) : selectFlow(flow))}
      />
      <Text variant="body2" weight="semibold" secondaryColor={!isCheckable}>
        <FlowIndex flow={flow} />
      </Text>
      <HStack gap={4}>
        {toDissociate ? (
          <ReconciliatedFlowDescription flow={flow} />
        ) : (
          <Text variant="body2" weight="regular" secondaryColor>
            {t('flow_payment_date')} {formatFromISOToDisplay(flow.expectedPaymentDate)}
          </Text>
        )}

        <Text variant="body2" weight="regular" secondaryColor>
          {flowStatus}
        </Text>

        <div className={styles.controlContainer}>
          {toDissociate ? (
            <DissociateButton
              onClick={() => onDissociate()}
              disabled={selected || isReconciledWithRegisteredTransaction}
            />
          ) : (
            <SplitButton onClick={() => onSplit()} disabled={selected} />
          )}
        </div>
      </HStack>

      <Amount amount={amount} weight="regular" />
    </div>
  );
};

const Amount = ({
  amount,
  secondaryColor,
  weight,
}: {
  amount: Money;
  secondaryColor?: boolean;
  weight?: 'bold' | 'regular';
}) => {
  return (
    <Text variant="body2" weight={weight || 'bold'} secondaryColor={secondaryColor} style={{ textAlign: 'right' }}>
      {formatMoney(amount)}
    </Text>
  );
};

const ReconciliatedFlowDescription = ({ flow }: { flow: Flow }) => {
  const validReconciliations = filterVerifiedReconciliations(flow.reconciliations);
  const length = validReconciliations.length;
  const reconciliation = validReconciliations[0];

  return length !== 1 || !reconciliation ? (
    <ReconciliatedFlowDescriptionMultiple length={length} />
  ) : (
    <ReconciliatedFlowDescriptionSingle reconciliation={reconciliation} />
  );
};

const ReconciliatedFlowDescriptionSingle = ({ reconciliation }: { reconciliation: Reconciliation }) => {
  const { t } = useTranslation('reconciliations');
  // The transaction is already eagerly loaded with the flow's reconciliations
  // (see decorateWithReconciliations in useDocumentInfiniteQuery), so we read it
  // directly instead of firing a per-row /api/v1/reconciliations request.
  const transaction = reconciliation.transaction;

  if (!isDefined(transaction)) return <ReconciliatedFlowDescriptionMultiple length={1} />;

  return (
    <div className={styles.reconciliatedFlowDescription}>
      <EllipsedText variant="body2" weight="regular" secondaryColor>
        {transaction.transactionType
          ? t('associated_to', {
              type: t('transaction_type.type', { context: transaction.transactionType, ns: 'transactions' }),
              date: formatFromISOToDisplay(transaction.date || ''),
            })
          : t('associated_to_no_type', { date: formatFromISOToDisplay(transaction.date || '') })}
      </EllipsedText>
    </div>
  );
};

const ReconciliatedFlowDescriptionMultiple = ({ length }: { length: number }) => {
  const { t } = useTranslation('reconciliations');
  return (
    <div className={styles.reconciliatedFlowDescription}>
      <Text variant="body2" weight="regular" secondaryColor>
        {t('associated_number', { length })}
      </Text>
      <InfoIconTooltip text={t('associated_details')} />
    </div>
  );
};

const DissociationFeedback = ({ onDismiss }: { onDismiss: () => void }) => {
  const { t } = useTranslation('reconciliations', { keyPrefix: 'reconcile_transaction_modal' });

  useEffect(() => {
    const timeout = setTimeout(onDismiss, 3000);
    return () => clearTimeout(timeout);
  }, [onDismiss]);

  return (
    <AlertMessage type="success" box fullWidth size="medium" className={styles.dissociationFeedback}>
      {t('document_dissociated')}
    </AlertMessage>
  );
};

const SearchResultCard = ({ document, flows }: { document: Document; flows: FlowWithDocument[] }) => {
  const { isSelected } = useReconcileModalContext();
  const [showReconciled] = useShowReconciledFlows();
  const [dissociated, setDissociated] = useState(false);

  const visibleFlows = flows.filter(
    (flow) => showReconciled || !hasVerifiedReconciliation(flow.reconciliations) || isSelected(flow),
  );

  return visibleFlows.length > 0 ? (
    <>
      <div className={styles.resultContainer}>
        <DocumentResume document={document} />

        {visibleFlows.map((flow) => (
          <FlowResume key={flow.id} flow={flow} onDissociateSuccess={() => setDissociated(true)} />
        ))}
      </div>

      {dissociated && <DissociationFeedback onDismiss={() => setDissociated(false)} />}
    </>
  ) : null;
};

export default memo(SearchResultCard);
